import { useState, useMemo, useEffect } from "react";

// Helper function to safely read nested object paths (e.g., "userDetails.userName.firstName")
const getNestedValue = (obj, path) => {
    if (!path) return "";
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};

export function useTableManager(data, searchKeys = [], defaultSortKey = "", defaultSortDir = "desc") {
    const [searchTerm, setSearchTerm] = useState("");
    const [sortConfig, setSortConfig] = useState({ key: defaultSortKey, direction: defaultSortDir });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Auto-reset to page 1 when searching or changing sort order
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, sortConfig]);

    const processedData = useMemo(() => {
        let sortableData = [...(data || [])];

        // 1. Apply Filter across all provided searchKeys
        if (searchTerm) {
            const lowercasedTerm = searchTerm.toLowerCase();
            sortableData = sortableData.filter((item) => {
                return searchKeys.some((key) => {
                    const val = getNestedValue(item, key);
                    return val && String(val).toLowerCase().includes(lowercasedTerm);
                });
            });
        }

        // 2. Apply Sort
        if (sortConfig.key) {
            sortableData.sort((a, b) => {
                const aVal = getNestedValue(a, sortConfig.key) || "";
                const bVal = getNestedValue(b, sortConfig.key) || "";
                
                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return sortableData;
    }, [data, sortConfig, searchTerm, searchKeys]); // Dependencies

    // Pagination metrics
    const totalPages = Math.ceil(processedData.length / itemsPerPage) || 1;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentData = processedData.slice(startIndex, startIndex + itemsPerPage);

    // Sort Handler
    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Pagination Handlers
    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(prev => prev + 1);
    };
    
    const handlePrevPage = () => {
        if (currentPage > 1) setCurrentPage(prev => prev - 1);
    };

    // UI Indicator Helper
    const getSortIndicator = (key) => {
        if (sortConfig.key !== key) return " ↕"; // Optional: Shows it is sortable
        return sortConfig.direction === 'asc' ? " ▲" : " ▼";
    };

    return {
        searchTerm, setSearchTerm,
        sortConfig, requestSort, getSortIndicator,
        currentPage, setCurrentPage, handleNextPage, handlePrevPage,
        itemsPerPage, setItemsPerPage,
        currentData, totalItems: processedData.length, totalPages, startIndex
    };
}